import { CONTROVERSY_SEVERITY_COLORS } from "../lib/constants.js";

interface ControversyCardProps {
  controversy: {
    id: string;
    title: string;
    description: string | null;
    severity: string;
    category: string;
    status: string;
    date_reported: string | null;
    source_url: string | null;
  };
}

function categoryLabel(cat: string): string {
  return cat.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export default function ControversyCard({ controversy }: ControversyCardProps) {
  const severityColor =
    CONTROVERSY_SEVERITY_COLORS[controversy.severity] ||
    "bg-slate-500/15 text-slate-400 border-slate-500/25";

  return (
    <div className="bg-slate-800/40 border border-slate-700/30 rounded-xl p-4 space-y-3">
      {/* Header badges */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`badge border text-[10px] font-semibold uppercase tracking-wide ${severityColor}`}>
          {controversy.severity}
        </span>
        <span className="badge bg-slate-700/40 text-slate-400 border border-slate-600/30 text-[10px]">
          {categoryLabel(controversy.category)}
        </span>
        <span
          className={`badge border text-[10px] ${
            controversy.status === "verified"
              ? "bg-red-500/10 text-red-400 border-red-500/20"
              : "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
          }`}
        >
          {controversy.status === "verified" ? "Verified" : "Alleged"}
        </span>
      </div>

      {/* Title */}
      <h4 className="font-semibold text-slate-200 text-sm leading-snug">
        {controversy.title}
      </h4>

      {/* Description */}
      {controversy.description && (
        <p className="text-slate-400 text-sm leading-relaxed">
          {controversy.description}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center gap-3 text-xs text-slate-500">
        {controversy.date_reported && (
          <span>{formatDate(controversy.date_reported)}</span>
        )}
        {controversy.source_url && (
          <a
            href={controversy.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-400 hover:text-indigo-300 transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            Source {"\u2197"}
          </a>
        )}
      </div>
    </div>
  );
}
